/**
 * NodeToolbar - Floating toolbar for creating nodes
 *
 * Features:
 * - Buttons for sticky note, rectangle, circle, triangle
 * - Creates nodes near canvas center (viewport aware, staggered to avoid stacking)
 * - Uses makeChange for undo/redo support
 * - AI Create button for AI-generated content (Phase 7)
 *
 * Position:
 * - Top-left corner of the canvas
 * - Fixed position (doesn't move with pan/zoom)
 * - High z-index to stay above canvas elements
 *
 * Why viewport center?
 * - New nodes appear where the user is looking
 * - Better UX than fixed coordinates (0,0) which might be off-screen
 * - Uses screenToFlowPosition to convert screen center to flow coordinates
 */
import { useCallback, useRef, useState, type RefObject } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';
import { nanoid } from 'nanoid';
import type * as Y from 'yjs';
import type { Node } from '@xyflow/react';
import { STICKY_COLORS, SHAPE_COLORS, type ShapeType } from '../nodes';
import { createDefaultBlockData, createDefaultGeneratorData, createDefaultKnowledgeNodeData, createDefaultChatNodeData, createDefaultDesignerNodeData, createDefaultAppScreenNodeData, createDefaultAppSettings, createDefaultCodeNodeData, createDefaultLooperNodeData, createDefaultJoinerNodeData, createDefaultCallBoardNodeData } from '@deep-agents/shared-types';
import type { AppWidget, AppSettings, TextWidget, ButtonWidget } from '@deep-agents/shared-types';
import { useImageUpload } from '../../hooks/useImageUpload';
import { useAIWriter } from '../../hooks/useAIWriter';

// ============================================================================
// TYPES
// ============================================================================

/**
 * NodeToolbarProps - Props for the NodeToolbar component
 *
 * @property nodesMap - Yjs map storing all nodes (for adding new nodes)
 * @property makeChange - Wrapper function for undo/redo support
 * @property onOpenTemplates - Callback to open template gallery modal
 * @property ydoc - Yjs document for BlockNode collaboration
 * @property awareness - Awareness for BlockNode cursor sync
 * @property boardId - Board ID for BlockNode comments
 * @property wsRef - WebSocket reference for AI requests (Phase 7)
 */
interface NodeToolbarProps {
  nodesMap: Y.Map<Node>;
  makeChange: (fn: () => void) => void;
  onOpenTemplates: () => void;
  ydoc?: Y.Doc;
  awareness?: any;
  boardId?: string;
  wsRef?: RefObject<WebSocket | null>;
}

// ============================================================================
// NODE TOOLBAR COMPONENT
// ============================================================================

/**
 * NodeToolbar - The toolbar UI for creating nodes
 *
 * How it works:
 * 1. User clicks a button (Note, Rectangle, Circle, Triangle)
 * 2. We calculate the center of the current viewport
 * 3. We convert that screen position to flow coordinates
 * 4. We create a new node at that position
 * 5. The node is added to the Yjs map via makeChange()
 * 6. Other collaborators see the node appear in real-time
 */
export function NodeToolbar({ nodesMap, makeChange, onOpenTemplates, ydoc, awareness, boardId, wsRef }: NodeToolbarProps) {
  // Get React Flow utilities for coordinate conversion
  const { screenToFlowPosition, getViewport } = useReactFlow();

  // AI prompt dialog state
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [isAIWorking, setIsAIWorking] = useState(false);

  // AI Writer hook for sending requests
  const { sendCanvasRequest } = useAIWriter({
    boardId: boardId || '',
    wsRef: wsRef || { current: null },
  });
  const { viewportWidth, viewportHeight } = useStore((state) => ({
    viewportWidth: state.width,
    viewportHeight: state.height,
  }));

  // File input ref for image upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const spawnIndexRef = useRef(0);

  // Image upload hook
  const { upload, isUploading } = useImageUpload();

  // ---------------------------------------------------------------------------
  // HELPER: Get position at center of current viewport
  // ---------------------------------------------------------------------------

  /**
   * getCenterPosition - Calculates flow coordinates at screen center
   *
   * This ensures new nodes appear where the user is looking, not at some
   * arbitrary fixed position that might be off-screen.
   */
  const getCenterPosition = useCallback(() => {
    if (viewportWidth > 0 && viewportHeight > 0) {
      const viewport = getViewport();
      return {
        x: (viewportWidth / 2 - viewport.x) / viewport.zoom,
        y: (viewportHeight / 2 - viewport.y) / viewport.zoom,
      };
    }

    // Fallback for initial render before React Flow reports dimensions.
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    return screenToFlowPosition({ x: centerX, y: centerY });
  }, [getViewport, viewportWidth, viewportHeight, screenToFlowPosition]);

  /**
   * getSpawnPosition - Slightly offsets each new node so repeated adds
   * don't stack perfectly on top of each other.
   */
  const getSpawnPosition = useCallback(() => {
    const center = getCenterPosition();
    const index = spawnIndexRef.current;
    spawnIndexRef.current += 1;

    if (index === 0) {
      return center;
    }

    const step = 28;
    const layer = Math.ceil((Math.sqrt(index + 1) - 1) / 2);
    const legLen = layer * 2;
    const maxIndexInLayer = (2 * layer + 1) ** 2 - 1;
    const legPos = maxIndexInLayer - index;
    const side = Math.floor(legPos / legLen);
    const offset = legPos % legLen;

    let dx = 0;
    let dy = 0;

    switch (side) {
      case 0:
        dx = layer;
        dy = -layer + offset;
        break;
      case 1:
        dx = layer - offset;
        dy = layer;
        break;
      case 2:
        dx = -layer;
        dy = layer - offset;
        break;
      default:
        dx = -layer + offset;
        dy = -layer;
        break;
    }

    return { x: center.x + dx * step, y: center.y + dy * step };
  }, [getCenterPosition]);

  // ---------------------------------------------------------------------------
  // NODE CREATION HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * addStickyNote - Creates a new sticky note at viewport center
   *
   * Default properties:
   * - Yellow background (classic sticky note color)
   * - 200x150 dimensions (comfortable for notes)
   * - Empty text (user double-clicks to add)
   */
  const addStickyNote = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'stickyNote',
      position,
      data: {
        text: '',
        color: STICKY_COLORS.yellow,
        width: 200,
        height: 150,
      },
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addShape - Creates a new shape at viewport center
   *
   * @param shapeType - 'rectangle' | 'circle' | 'triangle'
   *
   * Default properties:
   * - Blue fill (neutral, professional)
   * - 100x100 dimensions (square, good for all shapes)
   */
  const addShape = useCallback((shapeType: ShapeType) => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'shape',
      position,
      data: {
        shapeType,
        color: SHAPE_COLORS.blue,
        width: 100,
        height: 100,
      },
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addPlan - Creates a new plan node at viewport center
   *
   * Default properties:
   * - Empty tasks array (will be populated by agent)
   * - "AI Plan" title
   * - Violet theme to match AI/agent workflow category
   *
   * Use case:
   * - Manual plan tracking for AI agent execution
   * - Agent middleware will populate tasks via Yjs updates
   */
  const addPlan = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'plan',
      position,
      data: {
        tasks: [],
        currentTaskId: null,
        title: 'AI Plan',
      },
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addBlock - Creates a new block node at viewport center
   *
   * Default properties:
   * - Empty Tiptap content
   * - "Untitled Block" title
   * - Ready for rich text editing and data flow connections
   * - Runtime collaboration props are injected by BoardCanvas
   *
   * Use case:
   * - Collaborative document authoring
   * - Receiving data from upstream workflow nodes
   * - AI agent content generation target
   */
  const addBlock = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'block',
      position,
      data: createDefaultBlockData(),
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addGenerator - Creates a new generator node at viewport center
   *
   * Default properties:
   * - Claude Sonnet 4.5 model (balanced speed/intelligence)
   * - Empty prompt
   * - Temperature 0.7 (balanced creativity)
   * - Idle status (ready to generate)
   * - Includes ydoc/awareness/boardId for runtime functionality
   *
   * Use case:
   * - AI content generation with upstream context
   * - Flow-based prompt engineering
   * - Multi-step content pipelines
   */
  const addGenerator = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'generator',
      position,
      data: createDefaultGeneratorData(),
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addKnowledge - Creates a new knowledge node at viewport center
   *
   * Default properties:
   * - Idle status (ready for questions)
   * - Empty title
   * - Will auto-fetch board's knowledge space on mount
   *
   * Use case:
   * - Knowledge Space Q&A on the canvas
   * - Stream answers to connected BlockNodes
   * - View and manage documents
   */
  const addKnowledge = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'knowledge',
      position,
      data: createDefaultKnowledgeNodeData(),
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addChat - Creates a new chat node at viewport center
   *
   * Default properties:
   * - Idle status (ready for conversation)
   * - Empty title
   * - Messages stored in Yjs for collaboration
   *
   * Use case:
   * - Conversational AI on the canvas
   * - Stream responses to connected BlockNodes
   * - Pin AI messages to canvas as BlockNodes
   */
  const addChat = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'chat',
      position,
      data: createDefaultChatNodeData(),
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addDesigner - Creates a new designer node at viewport center
   *
   * Default properties:
   * - 12x8 grid (default size)
   * - Empty elements array
   * - "New Designer" title
   *
   * Use case:
   * - Freeform UI prototyping
   * - Drag-and-drop interface design
   * - Publishable interface building
   */
  const addDesigner = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'designer',
      position,
      data: createDefaultDesignerNodeData(),
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addCode - Creates a new code execution node at viewport center
   *
   * Default properties:
   * - Python language
   * - Empty code
   * - Idle status
   *
   * Use case:
   * - Run Python/JavaScript code in isolated sandbox
   * - Process data, generate outputs
   * - Jupyter-style code execution
   */
  const addCode = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();

    const newNode: Node = {
      id,
      type: 'code',
      position,
      data: createDefaultCodeNodeData(),
    };

    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addLooper - Creates a new looper node at viewport center
   *
   * Iterates over a list, executing a task for each item.
   * Inspired by Google Breadboard's Looper pattern.
   */
  const addLooper = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();

    const newNode: Node = {
      id,
      type: 'looper',
      position,
      data: createDefaultLooperNodeData(),
    };

    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addJoiner - Creates a new joiner node at viewport center
   *
   * Collects and merges outputs from Looper iterations.
   * Supports multiple merge modes: concatenate, use-last, user-select, ai-merge.
   */
  const addJoiner = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();

    const newNode: Node = {
      id,
      type: 'joiner',
      position,
      data: createDefaultJoinerNodeData(),
    };

    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addCallBoard - Creates a new call-board node at viewport center
   *
   * Calls another board as a tool/function.
   * Enables board composition for complex workflows.
   */
  const addCallBoard = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();

    const newNode: Node = {
      id,
      type: 'call-board',
      position,
      data: createDefaultCallBoardNodeData(),
    };

    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addAppScreen - Creates a new voice app screen node at viewport center
   *
   * Default properties:
   * - Empty widgets array
   * - "New Screen" title
   * - Not set as home screen (first screen added becomes home)
   * - Unique app ID generated
   *
   * Use case:
   * - Building voice-driven applications
   * - Connect screens for navigation
   * - Connect to agent nodes for AI capabilities
   */
  const addAppScreen = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();  // Generate unique ID

    const newNode: Node = {
      id,
      type: 'appScreen',
      position,
      data: createDefaultAppScreenNodeData(),
    };

    // Wrap in makeChange() so the creation can be undone
    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * loadCoffeeShopTemplate - Loads the Coffee Shop demo template
   *
   * Creates 5 connected app screens with widgets pre-configured:
   * - Home: Welcome + navigation buttons
   * - Menu: Drinks and food lists
   * - Popular: Featured items as cards
   * - About: Store info
   * - Order Confirm: Order confirmation
   */
  const loadCoffeeShopTemplate = useCallback(() => {
    const center = getCenterPosition();
    const appId = `app-${Date.now()}`;

    // Screen positions in a nice layout
    const positions = {
      home: { x: center.x, y: center.y },
      menu: { x: center.x - 400, y: center.y + 450 },
      popular: { x: center.x, y: center.y + 450 },
      about: { x: center.x + 400, y: center.y + 450 },
      orderConfirm: { x: center.x, y: center.y + 900 },
    };

    // App settings for the home screen
    const appSettings: AppSettings = {
      name: 'Bean & Brew',
      description: 'Your neighborhood coffee shop',
      voice: {
        voiceId: 'josh',
        greeting: 'Welcome to Bean & Brew! How can I help you today?',
        personality: 'You are a friendly barista helping customers browse the menu and place orders.',
      },
      theme: {
        primaryColor: '#78350F',
        darkMode: false,
      },
    };

    // Create node IDs
    const homeId = nanoid();
    const menuId = nanoid();
    const popularId = nanoid();
    const aboutId = nanoid();
    const orderConfirmId = nanoid();

    // Helper to create widgets
    type TextVariant = TextWidget['props']['variant'];
    type ButtonVariant = ButtonWidget['props']['variant'];

    const textWidget = (id: string, content: string, variant: TextVariant, order: number): AppWidget => ({
      id,
      type: 'text',
      order,
      props: { content, variant },
    });

    const buttonWidget = (id: string, label: string, variant: ButtonVariant, order: number, triggerPhrases?: string[]): AppWidget => ({
      id,
      type: 'button',
      order,
      props: { label, variant },
      voice: triggerPhrases ? { speakable: true, triggerPhrases } : undefined,
    });

    const listWidget = (id: string, order: number, items: Array<{ title: string; subtitle?: string }>): AppWidget => ({
      id,
      type: 'list',
      order,
      props: { layout: 'vertical' },
      previewItems: items,
    } as AppWidget);

    const cardWidget = (id: string, title: string, subtitle: string, order: number): AppWidget => ({
      id,
      type: 'card',
      order,
      props: { title, subtitle, variant: 'elevated' },
    });

    // Create all nodes
    const nodes: Node[] = [
      // Home Screen
      {
        id: homeId,
        type: 'appScreen',
        position: positions.home,
        data: createDefaultAppScreenNodeData({
          title: 'Home',
          appId,
          isHomeScreen: true,
          appSettings,
          widgets: [
            textWidget('logo', '☕', 'heading1', 0),
            textWidget('title', 'Bean & Brew', 'heading1', 1),
            textWidget('tagline', 'Your neighborhood coffee shop', 'body', 2),
            buttonWidget('menu-btn', 'View Menu', 'primary', 3, ['show menu', 'view menu']),
            buttonWidget('popular-btn', 'Popular Items', 'secondary', 4, ['popular', 'best sellers']),
            buttonWidget('about-btn', 'About Us', 'ghost', 5, ['about']),
          ],
        }),
      },
      // Menu Screen
      {
        id: menuId,
        type: 'appScreen',
        position: positions.menu,
        data: createDefaultAppScreenNodeData({
          title: 'Menu',
          appId,
          isHomeScreen: false,
          widgets: [
            textWidget('menu-title', 'Our Menu', 'heading1', 0),
            textWidget('drinks-header', '☕ Drinks', 'heading2', 1),
            listWidget('drinks-list', 2, [
              { title: 'Espresso', subtitle: '$3.50 - Rich and bold' },
              { title: 'Americano', subtitle: '$4.00 - Espresso with hot water' },
              { title: 'Latte', subtitle: '$5.00 - Smooth with steamed milk' },
              { title: 'Cappuccino', subtitle: '$5.00 - With foamed milk' },
              { title: 'Cold Brew', subtitle: '$4.50 - Smooth and refreshing' },
            ]),
            textWidget('food-header', '🥐 Food', 'heading2', 3),
            listWidget('food-list', 4, [
              { title: 'Croissant', subtitle: '$3.50 - Buttery and flaky' },
              { title: 'Blueberry Muffin', subtitle: '$3.00 - Fresh baked daily' },
            ]),
            buttonWidget('order-btn', 'Order a Latte', 'primary', 5, ['order latte']),
            buttonWidget('back-btn', '← Back', 'ghost', 6, ['go back']),
          ],
        }),
      },
      // Popular Screen
      {
        id: popularId,
        type: 'appScreen',
        position: positions.popular,
        data: createDefaultAppScreenNodeData({
          title: 'Popular',
          appId,
          isHomeScreen: false,
          widgets: [
            textWidget('popular-title', '⭐ Customer Favorites', 'heading1', 0),
            cardWidget('latte-card', '🥇 Latte', 'Our #1 bestseller - smooth espresso with perfectly steamed milk', 1),
            cardWidget('coldbrew-card', '🥈 Cold Brew', 'Perfect for hot days - smooth and refreshing', 2),
            cardWidget('croissant-card', '🥉 Croissant', 'Pairs perfectly with any coffee', 3),
            buttonWidget('order-btn', 'Order a Latte', 'primary', 4, ['order']),
            buttonWidget('full-menu-btn', 'See Full Menu', 'secondary', 5, ['full menu']),
            buttonWidget('back-btn', '← Back', 'ghost', 6, ['go back']),
          ],
        }),
      },
      // About Screen
      {
        id: aboutId,
        type: 'appScreen',
        position: positions.about,
        data: createDefaultAppScreenNodeData({
          title: 'About',
          appId,
          isHomeScreen: false,
          widgets: [
            textWidget('about-title', 'About Bean & Brew', 'heading1', 0),
            cardWidget('story-card', 'Our Story', 'Founded in 2020, we\'re passionate about great coffee and community.', 1),
            cardWidget('hours-card', '🕐 Hours', 'Mon-Fri: 6am - 8pm | Sat-Sun: 7am - 6pm', 2),
            cardWidget('location-card', '📍 Location', '123 Coffee Lane, Downtown District', 3),
            buttonWidget('back-btn', '← Back to Home', 'primary', 4, ['go back']),
          ],
        }),
      },
      // Order Confirmation Screen
      {
        id: orderConfirmId,
        type: 'appScreen',
        position: positions.orderConfirm,
        data: createDefaultAppScreenNodeData({
          title: 'Order Placed',
          appId,
          isHomeScreen: false,
          widgets: [
            textWidget('success-icon', '✓', 'heading1', 0),
            textWidget('success-title', 'Order Placed!', 'heading1', 1),
            cardWidget('order-details', 'Your Order', '1x Latte - $5.00 | Ready in ~3 minutes', 2),
            textWidget('order-number', 'Order #42', 'heading2', 3),
            textWidget('thanks', 'Thanks for ordering!', 'body', 4),
            buttonWidget('new-order-btn', 'Place Another Order', 'primary', 5, ['another order']),
            buttonWidget('done-btn', 'Done', 'ghost', 6, ['done']),
          ],
        }),
      },
    ];

    // Create edges connecting screens
    const edges = [
      // From Home
      { id: `e-${homeId}-${menuId}`, source: homeId, target: menuId },
      { id: `e-${homeId}-${popularId}`, source: homeId, target: popularId },
      { id: `e-${homeId}-${aboutId}`, source: homeId, target: aboutId },
      // From Menu to Order
      { id: `e-${menuId}-${orderConfirmId}`, source: menuId, target: orderConfirmId },
      // From Popular to Order and Menu
      { id: `e-${popularId}-${orderConfirmId}`, source: popularId, target: orderConfirmId },
      { id: `e-${popularId}-${menuId}`, source: popularId, target: menuId },
    ];

    // Add all nodes and edges in a single transaction
    makeChange(() => {
      // Add nodes
      for (const node of nodes) {
        nodesMap.set(node.id, node);
      }
    });

    // Note: Edges would need edgesMap to be passed as a prop
    // For now, users can manually connect the screens
    console.log('[Template] Loaded Coffee Shop template with', nodes.length, 'screens');
    console.log('[Template] Connect screens manually using the edge tool');
  }, [getCenterPosition, nodesMap, makeChange]);

  /**
   * loadBlackrollTemplate - Loads the Recovery Coach template
   *
   * Creates 5 connected app screens for a recovery coaching flow:
   * - Home: Welcome + entry points
   * - Interview/Consultation: Voice-driven Q&A about pain/goals
   * - Products: Recovery tool recommendations
   * - Analysis: Recovery profile summary
   * - Plan: 7-day weekly recovery routine
   */
  const loadBlackrollTemplate = useCallback(() => {
    const center = getCenterPosition();
    const appId = `app-${Date.now()}`;

    // Screen positions in a flow layout (top to bottom, branching)
    const positions = {
      home: { x: center.x, y: center.y },
      interview: { x: center.x - 300, y: center.y + 450 },
      products: { x: center.x + 300, y: center.y + 450 },
      analysis: { x: center.x, y: center.y + 900 },
      plan: { x: center.x, y: center.y + 1350 },
    };

    // App settings for the home screen
    const appSettings: AppSettings = {
      name: 'Recovery Coach',
      description: 'Your personal recovery & mobility coach - powered by voice',
      voice: {
        voiceId: 'rachel',
        greeting: 'Welcome to your Recovery Coach! I can help you find the right recovery tools and build a personalized plan. Would you like to start a consultation, or browse our products?',
        personality: `You are a professional recovery and mobility coach. You help users with foam rolling, mobility work, and recovery routines.

Your tone is warm, empathetic, and knowledgeable - like a trusted physiotherapist. You listen carefully and give evidence-based advice.

## Interview / Consultation Phase
When the user is on the Consultation screen:
- Ask about their pain points, tightness, stiffness, or recovery goals
- Be empathetic: "That sounds uncomfortable, let's work on that"
- Guide them to identify 2-3 target areas (e.g., lower back, shoulders, calves)
- Ask about: past injuries, activity level, how often they experience pain
- Keep questions conversational, one at a time
- After gathering enough info (2-3 exchanges), suggest moving to products

## Product Recommendations
When the user is on the Products screen:
- Standard Foam Roller: Best for beginners, full-body rolling, large muscle groups
- Mini Foam Roller: Targeted areas like IT band and calves, great for travel
- Massage Ball 08: Deep tissue work, trigger points, feet and glutes
- Duoball 08: Spine-safe rolling, neck and upper back, fits around vertebrae
- Fascia Gun: Percussive therapy, quick relief, adjustable intensity
- Loop Band: Active recovery, stretching, warm-up exercises
- Match products to their specific pain points and experience level

## Analysis Phase
When the user navigates to the Analysis screen:
- IMMEDIATELY call the update_recovery_profile tool to generate their personalized profile
- Include 4 items: Target Areas (from consultation), Goals (from what they said), Recommended Frequency, and Recommended Tools (matching their pain areas)
- After generating, briefly summarize what you learned
- Set realistic expectations (results take consistency, not just one session)

## Recovery Plan Phase
When the user navigates to the Recovery Plan screen:
- IMMEDIATELY call the update_recovery_plan tool to generate a personalized 7-day plan
- Base the plan on what you learned during the consultation: their pain areas, goals, and experience level
- Use the products that match their needs (foam roller, massage ball, duoball, loop band, etc.)
- Suggest 3-4 active sessions per week, 5-10 minutes each
- Alternate focus areas to avoid overworking one spot
- Include 2-3 rest/light stretch days
- Emphasize: slow rolling, breathe through discomfort, never roll directly on joints
- After generating, briefly explain why you chose those exercises

## General Rules
- Keep responses concise (2-3 sentences max for voice)
- If asked about medical issues, recommend consulting a doctor first
- Be encouraging: recovery is a journey, consistency matters most

## Updating the Recovery Plan
You have a tool called "update_recovery_plan" that lets you replace the 7-day plan.
When the user asks to change an exercise (e.g. "change day 4 to hip exercises"), call this tool
with the full 7-day plan as JSON, with the requested day(s) updated.
Each item needs a "title" (e.g. "Day 4 — Hip Openers") and "subtitle" (e.g. "Foam roller · 8 min · Hip flexor rolls").
Always send all 7 days, not just the changed one.
After updating, confirm what you changed.`,
      },
      agent: {
        clientTools: [
          {
            name: 'update_recovery_profile',
            description:
              'Generate the recovery profile summary based on the user\'s consultation. Call this automatically when the user reaches the Analysis screen. Send 4 items: Target Areas, Goals, Recommended Frequency, and Recommended Tools — all personalized from the consultation.',
            responseTemplate:
              'Recovery profile updated successfully. The user can now see their personalized profile on screen.',
            action: {
              type: 'setState' as const,
              path: 'analysis-list',
            },
            parameters: [
              {
                name: 'value',
                type: 'string' as const,
                description:
                  'JSON array of 4 profile items. Each needs "title" and "subtitle". Example: [{"title":"Target Areas","subtitle":"Lower Back · Shoulders · IT Band"},{"title":"Your Goals","subtitle":"Pain Relief · Improved Mobility"},{"title":"Recommended Frequency","subtitle":"3-4 sessions/week · 5-10 min each"},{"title":"Recommended Tools","subtitle":"Foam Roller · Massage Ball · Loop Band"}]',
                required: true,
              },
            ],
          },
          {
            name: 'update_recovery_plan',
            description:
              'Generate or update the 7-day recovery plan based on the user\'s consultation results. Call this automatically when the user reaches the Recovery Plan screen, and again whenever they request changes. Send the full 7-day plan as a JSON array. Each item must have "title" and "subtitle" fields.',
            responseTemplate:
              'Recovery plan updated successfully. The user can now see the new plan on screen.',
            action: {
              type: 'setState' as const,
              path: 'plan-list',
            },
            parameters: [
              {
                name: 'value',
                type: 'string' as const,
                description:
                  'JSON array of 7 plan items, each with "title" (e.g. "Day 1 — Lower Back Release") and "subtitle" (e.g. "Foam roller · 5 min · Slow rolls along spine")',
                required: true,
              },
            ],
          },
        ],
      },
      theme: {
        primaryColor: '#000000',
        darkMode: true,
      },
    };

    // Create node IDs
    const homeId = nanoid();
    const interviewId = nanoid();
    const productsId = nanoid();
    const analysisId = nanoid();
    const planId = nanoid();

    // Helper to create widgets with full voice & styling support
    type TextVariant = 'heading1' | 'heading2' | 'heading3' | 'body' | 'caption';
    type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
    type CardVariant = 'elevated' | 'outlined' | 'flat';
    type TextAlign = 'left' | 'center' | 'right';

    const textWidget = (id: string, content: string, variant: TextVariant, order: number, align?: TextAlign): AppWidget => ({
      id,
      type: 'text',
      order,
      props: { content, variant, ...(align ? { align } : {}) },
    });

    const buttonWidget = (
      id: string, label: string, variant: ButtonVariant, order: number,
      opts?: { triggerPhrases?: string[]; icon?: string }
    ): AppWidget => ({
      id,
      type: 'button',
      order,
      props: { label, variant, ...(opts?.icon ? { icon: opts.icon } : {}) },
      voice: opts?.triggerPhrases ? { speakable: true, triggerPhrases: opts.triggerPhrases } : undefined,
    });

    const listWidget = (
      id: string, order: number,
      items: Array<{ title: string; subtitle?: string }>,
      voiceDescription?: string
    ): AppWidget => ({
      id,
      type: 'list',
      order,
      props: { layout: 'vertical' },
      previewItems: items,
      voice: voiceDescription ? { speakable: true, description: voiceDescription } : undefined,
    } as AppWidget);

    const cardWidget = (
      id: string, title: string, subtitle: string, order: number,
      variant: CardVariant = 'elevated',
      voiceDescription?: string
    ): AppWidget => ({
      id,
      type: 'card',
      order,
      props: { title, subtitle, variant },
      voice: voiceDescription ? { speakable: true, description: voiceDescription } : undefined,
    });

    // Create all nodes
    const nodes: Node[] = [
      // Home Screen
      {
        id: homeId,
        type: 'appScreen',
        position: positions.home,
        data: createDefaultAppScreenNodeData({
          title: 'Home',
          appId,
          isHomeScreen: true,
          appSettings,
          widgets: [
            textWidget('brand-icon', '●', 'heading1', 0, 'center'),
            textWidget('title', 'Recovery Coach', 'heading1', 1, 'center'),
            textWidget('tagline', 'Personalized recovery & mobility coaching', 'body', 2, 'center'),
            buttonWidget('start-btn', 'Start Consultation', 'primary', 3, {
              icon: '💬',
              triggerPhrases: ['start consultation', 'start', 'begin', 'let\'s go', 'help me'],
            }),
            buttonWidget('products-btn', 'Browse Products', 'secondary', 4, {
              icon: '🛒',
              triggerPhrases: ['browse products', 'show products', 'products', 'shop'],
            }),
            buttonWidget('plan-btn', 'View Recovery Plan', 'ghost', 5, {
              icon: '📋',
              triggerPhrases: ['view plan', 'recovery plan', 'my plan', 'show plan'],
            }),
          ],
        }),
      },
      // Interview / Consultation Screen
      {
        id: interviewId,
        type: 'appScreen',
        position: positions.interview,
        data: createDefaultAppScreenNodeData({
          title: 'Consultation',
          appId,
          isHomeScreen: false,
          voice: {
            onEnterSpeak: 'Let\'s talk about your recovery needs. Tell me, where do you feel the most tightness or pain in your body?',
          },
          widgets: [
            textWidget('interview-title', 'Your Consultation', 'heading1', 0),
            textWidget('interview-instruction', 'Tell me about any pain, tightness, or areas you\'d like to work on. I\'ll help find the right recovery tools and routine for you.', 'body', 1),
            cardWidget('topic-card-1', 'Common Areas',
              'Lower back  ·  Shoulders  ·  Neck  ·  IT band  ·  Calves  ·  Glutes  ·  Feet', 2, 'outlined',
              'Common target areas include lower back, shoulders, neck, IT band, calves, glutes, and feet.'),
            cardWidget('topic-card-2', 'Recovery Goals',
              'Pain relief  ·  Better mobility  ·  Faster recovery  ·  Injury prevention  ·  Relaxation', 3, 'outlined',
              'Goals can include pain relief, better mobility, faster recovery, injury prevention, or relaxation.'),
            buttonWidget('continue-btn', 'See Product Recommendations', 'primary', 4, {
              icon: '→',
              triggerPhrases: ['show products', 'see products', 'recommendations', 'what do you recommend', 'continue'],
            }),
            buttonWidget('back-home-btn', '← Back', 'ghost', 5, {
              triggerPhrases: ['go back', 'back', 'home'],
            }),
          ],
        }),
      },
      // Products Screen
      {
        id: productsId,
        type: 'appScreen',
        position: positions.products,
        data: createDefaultAppScreenNodeData({
          title: 'Products',
          appId,
          isHomeScreen: false,
          voice: {
            onEnterSpeak: 'Here are our recommended recovery tools. I can tell you more about any product - just ask!',
          },
          widgets: [
            textWidget('products-title', 'Recommended Products', 'heading1', 0),
            textWidget('products-subtitle', 'Selected based on your needs. Ask me about any product for details.', 'caption', 1),
            listWidget('products-list', 2, [
              { title: 'Standard Foam Roller', subtitle: '$34.90 — Full-body rolling, ideal for beginners' },
              { title: 'Mini Foam Roller', subtitle: '$24.90 — Targeted relief, travel-friendly' },
              { title: 'Massage Ball 08', subtitle: '$12.90 — Deep tissue, trigger points' },
              { title: 'Duoball 08', subtitle: '$29.90 — Spine-safe, neck & back' },
              { title: 'Fascia Gun', subtitle: '$129.90 — Percussive therapy, adjustable intensity' },
              { title: 'Loop Band', subtitle: '$14.90 — Active recovery & stretching' },
            ], 'Our product lineup includes the Standard Foam Roller, Mini Foam Roller, Massage Ball, Duoball, Fascia Gun, and Loop Band.'),
            buttonWidget('analysis-btn', 'View My Analysis', 'primary', 3, {
              icon: '📊',
              triggerPhrases: ['view analysis', 'my analysis', 'show analysis', 'analyze', 'summary'],
            }),
            buttonWidget('back-interview-btn', '← Back', 'ghost', 4, {
              triggerPhrases: ['go back', 'back'],
            }),
          ],
        }),
      },
      // Analysis Screen
      {
        id: analysisId,
        type: 'appScreen',
        position: positions.analysis,
        data: createDefaultAppScreenNodeData({
          title: 'Analysis',
          appId,
          isHomeScreen: false,
          voice: {
            onEnterSpeak: 'Let me put together your recovery profile based on our conversation. One moment.',
          },
          widgets: [
            textWidget('analysis-title', 'Your Recovery Profile', 'heading1', 0),
            textWidget('analysis-subtitle', 'Personalized summary based on your consultation', 'caption', 1),
            listWidget('analysis-list', 2, [
              { title: 'Generating your profile...', subtitle: 'The agent will fill this in based on your consultation' },
            ], 'This profile will be dynamically populated by the agent using the update_recovery_profile tool after your consultation.'),
            buttonWidget('create-plan-btn', 'Create My Recovery Plan', 'primary', 3, {
              icon: '📋',
              triggerPhrases: ['create plan', 'make plan', 'show plan', 'recovery plan', 'build plan'],
            }),
            buttonWidget('back-products-btn', '← Back to Products', 'ghost', 4, {
              triggerPhrases: ['go back', 'back', 'products'],
            }),
          ],
        }),
      },
      // Recovery Plan Screen
      {
        id: planId,
        type: 'appScreen',
        position: positions.plan,
        data: createDefaultAppScreenNodeData({
          title: 'Recovery Plan',
          appId,
          isHomeScreen: false,
          voice: {
            onEnterSpeak: 'Let me create a personalized recovery plan based on your consultation. One moment while I put together your 7-day routine.',
          },
          widgets: [
            textWidget('plan-title', 'Your Recovery Plan', 'heading1', 0),
            textWidget('plan-subtitle', 'Your personalized 7-day routine — generated from your consultation', 'caption', 1),
            listWidget('plan-list', 2, [
              { title: 'Generating your plan...', subtitle: 'The agent will fill this in based on your consultation' },
            ], 'This list will be dynamically populated by the agent using the update_recovery_plan tool after your consultation.'),
            cardWidget('tips-card', 'Quick Tips',
              'Roll slowly (2-3 seconds per inch)  ·  Breathe deeply  ·  Never roll directly on joints  ·  Stop if you feel sharp pain', 3, 'outlined',
              'Key tips: roll slowly, breathe deeply, avoid joints, and stop if you feel sharp pain.'),
            buttonWidget('start-today-btn', 'Start Day 1', 'primary', 4, {
              icon: '▶',
              triggerPhrases: ['start today', 'start day 1', 'begin', 'let\'s start'],
            }),
            buttonWidget('back-home-plan-btn', '← Back to Home', 'ghost', 5, {
              triggerPhrases: ['go back', 'back', 'home', 'back to home'],
            }),
          ],
        }),
      },
    ];

    // Create edges connecting screens
    const edges = [
      // From Home
      { id: `e-${homeId}-${interviewId}`, source: homeId, target: interviewId },
      { id: `e-${homeId}-${productsId}`, source: homeId, target: productsId },
      { id: `e-${homeId}-${planId}`, source: homeId, target: planId },
      // Interview → Products
      { id: `e-${interviewId}-${productsId}`, source: interviewId, target: productsId },
      // Products → Analysis
      { id: `e-${productsId}-${analysisId}`, source: productsId, target: analysisId },
      // Analysis → Plan
      { id: `e-${analysisId}-${planId}`, source: analysisId, target: planId },
    ];

    // Add all nodes in a single transaction
    makeChange(() => {
      for (const node of nodes) {
        nodesMap.set(node.id, node);
      }
    });

    console.log('[Template] Loaded Recovery Coach template with', nodes.length, 'screens');
    console.log('[Template] Edge data:', edges.length, 'connections (connect screens manually using the edge tool)');

    // ── Pre-populate collections via API ──────────────────────
    // Fire-and-forget: create schemas + import items so they show
    // up in the Collections tab immediately.
    console.log('[Template] Creating collections for appId:', appId);
    (async () => {
      try {
        // --- Products collection ---
        const productsSchema = {
          name: 'products',
          schema: {
            name: 'products',
            label: 'Products',
            description: 'Recovery tools and accessories',
            fields: [
              { name: 'name', label: 'Name', type: 'string', required: true, showInList: true, searchable: true },
              { name: 'price', label: 'Price', type: 'number', required: true, showInList: true, sortable: true },
              { name: 'description', label: 'Description', type: 'string', showInDetail: true },
              { name: 'category', label: 'Category', type: 'enum', enumValues: ['roller', 'ball', 'band', 'gun'], filterable: true, showInList: true },
              { name: 'difficulty', label: 'Best For', type: 'enum', enumValues: ['beginner', 'intermediate', 'advanced'], filterable: true },
              { name: 'bodyAreas', label: 'Target Body Areas', type: 'array', arrayType: 'string', searchable: true },
            ],
            displayConfig: { titleField: 'name', subtitleField: 'description' },
          },
        };

        const productsItems = [
          { name: 'Standard Foam Roller', price: 34.90, description: 'Full-body rolling, ideal for beginners. Medium density for comfortable pressure.', category: 'roller', difficulty: 'beginner', bodyAreas: ['back', 'legs', 'glutes', 'calves'] },
          { name: 'Mini Foam Roller', price: 24.90, description: 'Targeted relief, travel-friendly. Perfect for IT band and calves.', category: 'roller', difficulty: 'beginner', bodyAreas: ['IT band', 'calves', 'arms'] },
          { name: 'Massage Ball 08', price: 12.90, description: 'Deep tissue work for trigger points. Great for feet, glutes, and shoulders.', category: 'ball', difficulty: 'intermediate', bodyAreas: ['feet', 'glutes', 'shoulders', 'chest'] },
          { name: 'Duoball 08', price: 29.90, description: 'Spine-safe rolling with groove for vertebrae. Ideal for neck and upper back.', category: 'ball', difficulty: 'intermediate', bodyAreas: ['neck', 'upper back', 'spine'] },
          { name: 'Fascia Gun', price: 129.90, description: 'Percussive therapy with adjustable intensity. Quick relief for sore muscles.', category: 'gun', difficulty: 'beginner', bodyAreas: ['full body', 'legs', 'shoulders', 'back'] },
          { name: 'Loop Band', price: 14.90, description: 'Active recovery and stretching. Versatile for warm-up and mobility exercises.', category: 'band', difficulty: 'beginner', bodyAreas: ['hips', 'glutes', 'shoulders', 'legs'] },
        ];

        // --- Exercises collection ---
        const exercisesSchema = {
          name: 'exercises',
          schema: {
            name: 'exercises',
            label: 'Exercises',
            description: 'Recovery and mobility exercises',
            fields: [
              { name: 'name', label: 'Name', type: 'string', required: true, showInList: true, searchable: true },
              { name: 'duration', label: 'Duration (min)', type: 'number', required: true, showInList: true, sortable: true },
              { name: 'bodyArea', label: 'Body Area', type: 'enum', enumValues: ['lower back', 'upper back', 'shoulders', 'neck', 'IT band', 'quads', 'calves', 'glutes', 'hips', 'feet', 'full body'], filterable: true, showInList: true },
              { name: 'tool', label: 'Tool Required', type: 'enum', enumValues: ['foam roller', 'massage ball', 'duoball', 'fascia gun', 'loop band', 'none'], filterable: true },
              { name: 'difficulty', label: 'Difficulty', type: 'enum', enumValues: ['beginner', 'intermediate', 'advanced'], filterable: true },
              { name: 'instructions', label: 'Instructions', type: 'string', showInDetail: true },
              { name: 'tips', label: 'Tips', type: 'string', showInDetail: true },
            ],
            displayConfig: { titleField: 'name', subtitleField: 'bodyArea' },
          },
        };

        const exercisesItems = [
          { name: 'Lower Back Release', duration: 5, bodyArea: 'lower back', tool: 'foam roller', difficulty: 'beginner', instructions: 'Lie on your back with the roller under your lower back. Slowly roll from lower ribs to pelvis. Pause on tender spots for 20-30 seconds.', tips: 'Keep core engaged. Breathe deeply. Avoid rolling directly on the spine.' },
          { name: 'Shoulder Mobilization', duration: 5, bodyArea: 'shoulders', tool: 'massage ball', difficulty: 'intermediate', instructions: 'Place the ball between your shoulder blade and a wall. Apply gentle pressure and make small circles. Move to different spots around the shoulder blade.', tips: 'Start with light pressure and increase gradually. Focus on trigger points.' },
          { name: 'IT Band Roll', duration: 8, bodyArea: 'IT band', tool: 'foam roller', difficulty: 'intermediate', instructions: 'Lie on your side with the roller under your outer thigh. Roll from hip to just above the knee. Use your top leg for support and pressure control.', tips: 'This can be intense — start gently. Never roll over the knee joint.' },
          { name: 'Upper Back & Neck Relief', duration: 5, bodyArea: 'upper back', tool: 'duoball', difficulty: 'beginner', instructions: 'Place the duoball on either side of your spine at upper back level. Gently roll up toward the neck, letting the groove protect your vertebrae.', tips: 'Keep movements slow. Stop at the base of the skull — never roll the neck itself.' },
          { name: 'Full Body Flow', duration: 10, bodyArea: 'full body', tool: 'foam roller', difficulty: 'beginner', instructions: 'Start at calves and work up: calves → quads → IT band → glutes → lower back → upper back. Spend 1-2 minutes per area.', tips: 'Great as a daily routine. Skip any area that feels painful rather than tight.' },
          { name: 'Hip Opener', duration: 8, bodyArea: 'hips', tool: 'foam roller', difficulty: 'intermediate', instructions: 'Sit on the roller with one ankle crossed over the opposite knee. Lean toward the crossed leg and roll slowly over the glute and hip area.', tips: 'Excellent for desk workers. Combine with hip flexor stretches for best results.' },
          { name: 'Calf Release', duration: 5, bodyArea: 'calves', tool: 'foam roller', difficulty: 'beginner', instructions: 'Sit with one calf on the roller. Cross the other leg on top for extra pressure. Roll from ankle to just below the knee.', tips: 'Rotate your leg inward and outward to hit different parts of the calf.' },
          { name: 'Foot Massage', duration: 3, bodyArea: 'feet', tool: 'massage ball', difficulty: 'beginner', instructions: 'Stand with the ball under one foot. Apply gentle pressure and roll from heel to toes. Pause on the arch for extra relief.', tips: 'Great first thing in the morning or after long periods of standing.' },
          { name: 'Quad Release', duration: 5, bodyArea: 'quads', tool: 'foam roller', difficulty: 'beginner', instructions: 'Lie face down with the roller under your thighs. Roll from hip to just above the knee. Slightly angle your body to target inner and outer quads.', tips: 'Keep your core engaged to control pressure. Breathe through tight spots.' },
          { name: 'Active Recovery Stretch', duration: 5, bodyArea: 'full body', tool: 'loop band', difficulty: 'beginner', instructions: 'Use the loop band for assisted stretching: hamstring pulls, shoulder dislocates, and hip flexor stretches. Hold each stretch 20-30 seconds.', tips: 'Focus on breathing into the stretch. Never bounce or force the movement.' },
        ];

        // Create schemas first, then import items
        await fetch(`/api/apps/${appId}/collections`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(productsSchema),
        });

        await fetch(`/api/apps/${appId}/collections`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(exercisesSchema),
        });

        // Import items
        await fetch(`/api/apps/${appId}/collections/products/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ items: productsItems, format: 'json' }),
        });

        await fetch(`/api/apps/${appId}/collections/exercises/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ items: exercisesItems, format: 'json' }),
        });

        console.log('[Template] Pre-populated 2 collections: products (6 items), exercises (10 items)');
      } catch (err) {
        console.warn('[Template] Failed to pre-populate collections (API may not be running):', err);
      }
    })();
  }, [getCenterPosition, nodesMap, makeChange]);

  /**
   * addIPod - Creates a new iPod node at viewport center
   *
   * A fun classic iPod music player that plays tones!
   */
  const addIPod = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();

    const newNode: Node = {
      id,
      type: 'ipod',
      position,
      data: {
        title: 'My iPod',
      },
    };

    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addForm - Creates a new form node at viewport center
   *
   * Default properties:
   * - Empty content (user edits inline)
   * - "Form" label
   */
  const addForm = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();

    const newNode: Node = {
      id,
      type: 'form',
      position,
      data: {
        label: 'Form',
        content: '',
        fieldCount: 0,
      },
    };

    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * addProcessor - Creates a generic workflow node at viewport center
   *
   * Uses the PortNode renderer with minimal config until node definitions land.
   */
  const addProcessor = useCallback(() => {
    const position = getSpawnPosition();
    const id = nanoid();

    const newNode: Node = {
      id,
      type: 'processor',
      position,
      data: {
        definitionType: 'custom',
        config: {},
        label: 'Processor',
      },
    };

    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  /**
   * handleFileSelect - Handle image file selection
   *
   * Workflow:
   * 1. User clicks image button -> opens file picker
   * 2. User selects image -> this handler fires
   * 3. Upload image to R2 via useImageUpload hook
   * 4. Create ImageNode with the proxy URL
   * 5. Reset input for next upload
   */
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Upload to R2
    const result = await upload(file);
    if (result) {
      const position = getSpawnPosition();
      const id = nanoid();

      // Create image node with proxy URL
      const newNode: Node = {
        id,
        type: 'image',
        position,
        data: {
          src: result.proxyUrl,
          alt: file.name,
          width: 300,
          height: 200,
        },
      };

      // Add to Yjs map with undo support
      makeChange(() => {
        nodesMap.set(id, newNode);
      });
    }

    // Reset input to allow re-uploading same file
    e.target.value = '';
  }, [upload, getSpawnPosition, nodesMap, makeChange]);

  // ---------------------------------------------------------------------------
  // AI CREATE HANDLER
  // ---------------------------------------------------------------------------

  /**
   * handleAICreate - Opens AI prompt dialog
   * User enters what they want AI to create, then we send the request.
   */
  const handleAICreate = useCallback(() => {
    setShowAIPrompt(true);
    setAIPrompt('');
  }, []);

  /**
   * handleAISubmit - Sends AI canvas request when user submits prompt
   */
  const handleAISubmit = useCallback(() => {
    if (!aiPrompt.trim()) {
      return;
    }

    // Get position at viewport center
    const position = getCenterPosition();

    // Send request to AI via WebSocket
    sendCanvasRequest({
      action: 'generate',
      context: aiPrompt.trim(),
      position,
      nodeType: 'sticky',
    });

    // Close dialog and reset state
    setShowAIPrompt(false);
    setAIPrompt('');
    setIsAIWorking(true);

    // Reset working state after a timeout (AI presence will show actual status)
    setTimeout(() => setIsAIWorking(false), 5000);
  }, [aiPrompt, getCenterPosition, sendCanvasRequest]);

  /**
   * handleAIKeyDown - Handle Enter to submit, Escape to cancel
   */
  const handleAIKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAISubmit();
    } else if (e.key === 'Escape') {
      setShowAIPrompt(false);
      setAIPrompt('');
    }
  }, [handleAISubmit]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div className="node-panel">
      <div className="node-panel-header">
        <div>
          <div className="node-panel-title">Nodes</div>
          <div className="node-panel-subtitle">Create on canvas</div>
        </div>
      </div>

      <div className="node-panel-scroll">
        <div className="node-panel-section">
          <div className="node-panel-section-title">Basic</div>
          <button type="button" onClick={addStickyNote} className="node-panel-button" title="Add sticky note">
            <span className="node-panel-icon node-note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="7" y1="8" x2="17" y2="8" />
                <line x1="7" y1="12" x2="14" y2="12" />
              </svg>
            </span>
            <span>Sticky note</span>
          </button>

          <div className="node-panel-shapes">
            <button type="button" onClick={() => addShape('rectangle')} className="node-panel-shape" title="Add rectangle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="6" width="16" height="12" rx="2" />
              </svg>
            </button>
            <button type="button" onClick={() => addShape('circle')} className="node-panel-shape" title="Add circle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="7" />
              </svg>
            </button>
            <button type="button" onClick={() => addShape('triangle')} className="node-panel-shape" title="Add triangle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12,4 20,19 4,19" />
              </svg>
            </button>
          </div>
        </div>

        {/* AI Create Section - Phase 7 Ghost Writer */}
        <div className="node-panel-section">
          <div className="node-panel-section-title">AI Create</div>
          <button
            type="button"
            onClick={handleAICreate}
            disabled={isAIWorking || !wsRef?.current}
            className="node-panel-button"
            title="Create with AI"
          >
            <span className="node-panel-icon node-ai">
              {/* Sparkles icon for AI */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </span>
            <span>{isAIWorking ? 'AI working...' : 'Generate with AI'}</span>
          </button>
        </div>

        {/* AI Prompt Dialog - Centered Modal */}
        {showAIPrompt && (
          <>
            {/* Backdrop */}
            <div
              className="node-panel-ai-backdrop"
              onClick={() => setShowAIPrompt(false)}
            />
            {/* Modal Dialog */}
            <div className="node-panel-ai-dialog">
              <div className="node-panel-ai-dialog-title">What should AI create?</div>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAIPrompt(e.target.value)}
                onKeyDown={handleAIKeyDown}
                placeholder="e.g., 'Brainstorm ideas for a marketing campaign' or 'Create a flowchart for user onboarding'"
                className="node-panel-ai-input"
                autoFocus
              />
              <div className="node-panel-ai-actions">
                <button
                  type="button"
                  onClick={() => setShowAIPrompt(false)}
                  className="node-panel-ai-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAISubmit}
                  disabled={!aiPrompt.trim()}
                  className="node-panel-ai-submit"
                >
                  Create
                </button>
              </div>
            </div>
          </>
        )}

        <div className="node-panel-section">
          <div className="node-panel-section-title">Workflow</div>
          <button type="button" onClick={addProcessor} className="node-panel-button" title="Add processor node">
            <span className="node-panel-icon node-processor">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="4" width="16" height="16" rx="3" />
                <path d="M8 8h8M8 12h8M8 16h5" />
              </svg>
            </span>
            <span>Processor</span>
          </button>
          <button type="button" onClick={addForm} className="node-panel-button" title="Add form node">
            <span className="node-panel-icon node-form">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="4" width="14" height="16" rx="2" />
                <line x1="8" y1="9" x2="16" y2="9" />
                <line x1="8" y1="13" x2="14" y2="13" />
              </svg>
            </span>
            <span>Form</span>
          </button>
          <button type="button" onClick={addPlan} className="node-panel-button" title="Add plan node">
            <span className="node-panel-icon node-plan">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <path d="M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                <path d="M9 12h.01M9 16h.01M12 12h5M12 16h5" />
              </svg>
            </span>
            <span>Plan</span>
          </button>
          <button type="button" onClick={addBlock} className="node-panel-button" title="Add content block">
            <span className="node-panel-icon node-block">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="4" width="16" height="16" rx="2" />
                <path d="M4 10h16M9 4v16" />
                <circle cx="14" cy="14" r="2" />
              </svg>
            </span>
            <span>Block</span>
          </button>
          <button type="button" onClick={addGenerator} className="node-panel-button" title="Add AI generator">
            <span className="node-panel-icon node-generator">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </span>
            <span>Generator</span>
          </button>
          <button type="button" onClick={addKnowledge} className="node-panel-button" title="Add knowledge space">
            <span className="node-panel-icon node-knowledge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </span>
            <span>Knowledge</span>
          </button>
          <button type="button" onClick={addChat} className="node-panel-button" title="Add chat">
            <span className="node-panel-icon node-chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            <span>Chat</span>
          </button>
          <button type="button" onClick={addCode} className="node-panel-button" title="Add code execution node">
            <span className="node-panel-icon node-code">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
            </span>
            <span>Code</span>
          </button>
          <button type="button" onClick={addLooper} className="node-panel-button" title="Add loop iteration node">
            <span className="node-panel-icon node-looper">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                <path d="M21 7L17 3" />
                <path d="M12 8L16 12" />
              </svg>
            </span>
            <span>Looper</span>
          </button>
          <button type="button" onClick={addJoiner} className="node-panel-button" title="Add output merge node">
            <span className="node-panel-icon node-joiner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
            </span>
            <span>Joiner</span>
          </button>
          <button type="button" onClick={addCallBoard} className="node-panel-button" title="Add board call node">
            <span className="node-panel-icon node-callboard">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 9l6 3-6 3V9z" />
              </svg>
            </span>
            <span>Call Board</span>
          </button>
          <button type="button" onClick={addDesigner} className="node-panel-button" title="Add designer canvas">
            <span className="node-panel-icon node-designer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                <path d="M4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                <path d="M16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
            </span>
            <span>Designer</span>
          </button>
        </div>

        <div className="node-panel-section">
          <div className="node-panel-section-title">Media</div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="node-panel-button"
            title="Upload image"
          >
            <span className="node-panel-icon node-media">
              {isUploading ? (
                <svg viewBox="0 0 24 24" className="node-panel-spinner">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.35" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="4" y="5" width="16" height="14" rx="2" />
                  <path d="M8 13l3-3 5 5" />
                  <circle cx="9" cy="9" r="1.5" />
                </svg>
              )}
            </span>
            <span>Image</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <div className="node-panel-section">
          <div className="node-panel-section-title">Voice Apps</div>
          <button type="button" onClick={addAppScreen} className="node-panel-button" title="Add voice app screen">
            <span className="node-panel-icon node-appscreen">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="2" width="14" height="20" rx="3" />
                <path d="M12 18h.01" />
                <path d="M9 6h6M9 9h6M9 12h4" />
              </svg>
            </span>
            <span>App Screen</span>
          </button>
          <button type="button" onClick={loadBlackrollTemplate} className="node-panel-button" title="Load Recovery Coach template (5 screens)">
            <span className="node-panel-icon node-appscreen">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4l2 2" />
                <path d="M8 16l1-1M16 16l-1-1" />
              </svg>
            </span>
            <span>Recovery Coach</span>
          </button>
        </div>

        <div className="node-panel-section">
          <div className="node-panel-section-title">Templates</div>
          <button type="button" onClick={onOpenTemplates} className="node-panel-button" title="Open templates">
            <span className="node-panel-icon node-template">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="4" width="7" height="7" rx="1.5" />
                <rect x="13" y="4" width="7" height="7" rx="1.5" />
                <rect x="4" y="13" width="7" height="7" rx="1.5" />
                <rect x="13" y="13" width="7" height="7" rx="1.5" />
              </svg>
            </span>
            <span>Template gallery</span>
          </button>
        </div>

        <div className="node-panel-section">
          <div className="node-panel-section-title">Fun</div>
          <button type="button" onClick={addIPod} className="node-panel-button" title="Add iPod music player">
            <span className="node-panel-icon node-ipod">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="2" width="14" height="20" rx="3" />
                <rect x="8" y="5" width="8" height="5" rx="1" />
                <circle cx="12" cy="15" r="4" />
                <circle cx="12" cy="15" r="1.5" />
              </svg>
            </span>
            <span>iPod</span>
          </button>
        </div>
      </div>

      <style>{`
        .node-panel {
          position: absolute;
          top: 16px;
          left: 16px;
          bottom: 16px;
          width: 200px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 16px;
          box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
          display: flex;
          flex-direction: column;
          z-index: 12;
        }

        .node-panel-header {
          padding: 14px 16px 10px;
          border-bottom: 1px solid var(--color-border);
        }

        .node-panel-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--color-text);
        }

        .node-panel-subtitle {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--color-text-muted);
          margin-top: 4px;
        }

        .node-panel-scroll {
          padding: 12px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .node-panel-section-title {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--color-text-muted);
          margin-bottom: 8px;
        }

        .node-panel-button {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border-radius: 10px;
          border: 1px solid transparent;
          background: transparent;
          color: var(--color-text);
          font-size: 13px;
          font-weight: 500;
          text-align: left;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }

        .node-panel-button:hover:not(:disabled) {
          background: var(--color-accent-soft);
          border-color: var(--color-accent-soft-strong);
          color: var(--color-text);
        }

        .node-panel-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .node-panel-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          display: grid;
          place-items: center;
          background: var(--color-surface-muted);
          color: var(--color-text-secondary);
          flex-shrink: 0;
        }

        .node-panel-icon svg {
          width: 16px;
          height: 16px;
        }

        .node-note {
          background: #fef9c3;
          color: #854d0e;
        }

        .node-processor {
          background: var(--color-accent-soft);
          color: var(--color-accent);
        }

        .node-form {
          background: #e0f2fe;
          color: #0369a1;
        }

        .node-plan {
          background: #ede9fe;
          color: #6d28d9;
        }

        .node-block {
          background: #f0f9ff;
          color: #0369a1;
        }

        .node-generator {
          background: #ede9fe;
          color: #7c3aed;
        }

        .node-knowledge {
          background: #ecfeff;
          color: #0891b2;
        }

        .node-chat {
          background: #fffbeb;
          color: #d97706;
        }

        .node-designer {
          background: #f5f3ff;
          color: #7c3aed;
        }

        .node-appscreen {
          background: #f3e8ff;
          color: #8b5cf6;
        }

        .node-code {
          background: #d1fae5;
          color: #059669;
        }

        .node-looper {
          background: #fef3c7;
          color: #d97706;
        }

        .node-joiner {
          background: #ccfbf1;
          color: #0d9488;
        }

        .node-callboard {
          background: #cffafe;
          color: #0891b2;
        }

        .node-media {
          background: #ecfeff;
          color: #0e7490;
        }

        .node-template {
          background: #f1f5f9;
          color: #334155;
        }

        .node-ipod {
          background: linear-gradient(145deg, #f5f5f5, #e0e0e0);
          color: #555;
        }

        /* AI Create button - violet theme to match AI nodes */
        .node-ai {
          background: #ede9fe;
          color: #7c3aed;
        }

        /* AI Prompt Dialog - Centered Modal */
        .node-panel-ai-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.4);
          z-index: 999;
          animation: fadeIn 0.15s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .node-panel-ai-dialog {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 90%;
          max-width: 500px;
          padding: 20px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
          z-index: 1000;
          animation: slideIn 0.2s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translate(-50%, -45%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }

        .node-panel-ai-dialog-title {
          font-size: 13px;
          font-weight: 600;
          color: var(--color-text);
          margin-bottom: 8px;
        }

        .node-panel-ai-input {
          width: 100%;
          min-height: 80px;
          padding: 10px 12px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          background: var(--color-surface-muted);
          color: var(--color-text);
          font-size: 13px;
          font-family: inherit;
          resize: vertical;
        }

        .node-panel-ai-input:focus {
          outline: none;
          border-color: #7c3aed;
          box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.15);
        }

        .node-panel-ai-input::placeholder {
          color: var(--color-text-muted);
        }

        .node-panel-ai-actions {
          display: flex;
          gap: 8px;
          margin-top: 10px;
          justify-content: flex-end;
        }

        .node-panel-ai-cancel {
          padding: 6px 14px;
          border-radius: 6px;
          border: 1px solid var(--color-border);
          background: transparent;
          color: var(--color-text-secondary);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .node-panel-ai-cancel:hover {
          background: var(--color-surface-muted);
        }

        .node-panel-ai-submit {
          padding: 6px 14px;
          border-radius: 6px;
          border: none;
          background: #7c3aed;
          color: white;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .node-panel-ai-submit:hover:not(:disabled) {
          background: #6d28d9;
        }

        .node-panel-ai-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .node-panel-shapes {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }

        .node-panel-shape {
          width: 100%;
          border-radius: 10px;
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          height: 40px;
          display: grid;
          place-items: center;
          color: var(--color-text-secondary);
          transition: border-color 0.15s ease, background 0.15s ease;
        }

        .node-panel-shape:hover {
          border-color: var(--color-accent);
          background: var(--color-accent-soft);
        }

        .node-panel-shape svg {
          width: 18px;
          height: 18px;
        }

        .node-panel-spinner {
          width: 16px;
          height: 16px;
          animation: node-panel-spin 0.9s linear infinite;
        }

        @keyframes node-panel-spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 960px) {
          .node-panel {
            width: 168px;
            top: 12px;
            left: 12px;
            bottom: 12px;
          }
        }
      `}</style>
    </div>
  );
}
